'use client';

import { useEffect, useRef, useState } from 'react';
import { createLogger } from '@/lib/production-logger';
import { useRouter, usePathname } from 'next/navigation';
import { toast } from 'sonner';
import ChangelogModal, { useChangelogCheck } from './ChangelogModal';

/**
 * 认证守卫组件
 * 功能：
 * 1. 在组件挂载时检查JWT是否有效
 * 2. 只有认证通过才渲染子组件
 * 3. 定期检查JWT（每30秒）
 * 4. Access Token 过期时自动刷新
 * 5. Refresh Token 失效后才跳转到登录页
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isCheckingRef = useRef(false);
    const isRefreshingRef = useRef(false); // 防止重复刷新
    const changelogCheckedRef = useRef(false);

    // 🔒 关键修复：添加状态控制，未认证前不渲染children
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // 更新日志检查
    const { changelogs, showModal, checkUnread, markRead, closeModal } = useChangelogCheck();

    /**
     * 尝试刷新 Access Token
     * @returns true 如果刷新成功，false 如果失败
     */
    const tryRefreshToken = async (): Promise<boolean> => {
        // 防止重复刷新
        if (isRefreshingRef.current) {
            return false;
        }

        isRefreshingRef.current = true;

        try {
            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    console.log('[AuthGuard] Token 刷新成功');
                    return true;
                }
            }

            console.log('[AuthGuard] Token 刷新失败');
            return false;
        } catch (error) {
            console.error('[AuthGuard] Token 刷新请求失败:', error);
            return false;
        } finally {
            isRefreshingRef.current = false;
        }
    };

    const checkAuth = async (isInitialCheck: boolean = false, retryAfterRefresh: boolean = false) => {
        // 防止重复检查
        if (isCheckingRef.current && !isInitialCheck && !retryAfterRefresh) {
            return;
        }

        isCheckingRef.current = true;

        try {
            const response = await fetch('/api/admin/login', {
                method: 'GET',
                credentials: 'include'
            });

            if (response.ok) {
                // 🔒 认证成功
                const result = await response.json();
                if (result.success && result.user) {
                    setIsAuthenticated(true);
                    // 更新sessionStorage（仅用于显示）
                    sessionStorage.setItem('adminLoggedIn', 'true');
                    sessionStorage.setItem('adminLoginTime', Date.now().toString());
                    sessionStorage.setItem('adminUser', JSON.stringify(result.user));

                    // 检查未读的更新日志（仅首次认证成功时）
                    if (isInitialCheck && !changelogCheckedRef.current) {
                        changelogCheckedRef.current = true;
                        // 延迟一点执行，确保页面已加载
                        setTimeout(() => {
                            checkUnread();
                        }, 500);
                    }
                } else {
                    // 认证失败
                    clearAuthAndRedirect();
                }
            } else {
                // JWT过期或无效 - 尝试解析错误信息
                let errorMessage = '';
                let redirectReason = '';
                try {
                    const result = await response.json();
                    errorMessage = result.error || '';
                } catch (e) {
                    // 无法解析响应，使用空字符串
                }

                // 🔄 Token 轮换：如果是 Token 过期，尝试刷新
                // 仅在非重试模式下尝试刷新（避免无限循环）
                if (!retryAfterRefresh && response.status === 401) {
                    // 会话被撤销的情况不尝试刷新
                    if (!errorMessage.includes('Session expired or revoked')) {
                        console.log('[AuthGuard] Access Token 过期，尝试刷新...');
                        const refreshSuccess = await tryRefreshToken();
                        if (refreshSuccess) {
                            // 刷新成功，重新检查认证
                            isCheckingRef.current = false;
                            await checkAuth(isInitialCheck, true);
                            return;
                        }
                        console.log('[AuthGuard] 刷新失败，需要重新登录');
                    }
                }

                // 🔔 根据错误类型显示不同的通知
                if (!isInitialCheck) {
                    // 只在非初始检查时显示toast（避免页面加载时的干扰）
                    if (errorMessage.includes('Session expired or revoked')) {
                        // 会话被撤销（另一台设备登录）
                        toast.error('您的账号已在其他设备登录', {
                            description: '为了账号安全，当前会话已失效',
                            duration: 3000,
                        });
                        redirectReason = 'session_revoked';
                    } else if (errorMessage.includes('Invalid token') || errorMessage.includes('Token verification failed')) {
                        // Token无效
                        toast.warning('登录已过期', {
                            description: '请重新登录',
                            duration: 3000,
                        });
                        redirectReason = 'token_expired';
                    } else if (errorMessage) {
                        // 其他认证错误
                        toast.warning('会话已失效', {
                            description: '请重新登录',
                            duration: 3000,
                        });
                        redirectReason = 'auth_failed';
                    }
                }

                // 延迟500ms后重定向，确保toast可见
                setTimeout(() => {
                    clearAuthAndRedirect(redirectReason);
                }, 500);
            }
        } catch (error) {
            console.error('认证检查失败:', error);
            // 🔒 网络错误也应该重定向到登录页
            if (isInitialCheck) {
                clearAuthAndRedirect();
            } else {
                // 非初始检查时，网络错误不显示toast（避免在网络不稳定时的干扰）
                setTimeout(() => {
                    clearAuthAndRedirect();
                }, 500);
            }
        } finally {
            isCheckingRef.current = false;
            if (isInitialCheck) {
                setIsLoading(false);
            }
        }
    };

    const clearAuthAndRedirect = (reason?: string) => {
        // 清除所有缓存
        sessionStorage.removeItem('adminLoggedIn');
        sessionStorage.removeItem('adminLoginTime');
        sessionStorage.removeItem('adminUser');
        sessionStorage.removeItem('adminToken');

        // 清除 JWT cookies（包括 access token 和 refresh token）
        document.cookie = 'admin-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict';
        document.cookie = 'refresh-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict';

        // 🔒 保存当前路径用于登录后重定向
        const currentPath = pathname || '';
        let redirectUrl = '/admin-2025/login';

        // 排除登录页和紧急登录页，不将它们作为重定向目标
        if (currentPath && currentPath.startsWith('/admin-2025') &&
            !currentPath.includes('/login') && !currentPath.includes('/emergency-login')) {
            const params = new URLSearchParams();
            params.set('redirect', currentPath);
            if (reason) {
                params.set('reason', reason);
            }
            redirectUrl = `/admin-2025/login?${params.toString()}`;
        } else if (reason) {
            redirectUrl = `/admin-2025/login?reason=${encodeURIComponent(reason)}`;
        }

        router.replace(redirectUrl);
        setIsAuthenticated(false);
    };

    useEffect(() => {
        // 跳过登录页和紧急登录页
        if (pathname?.includes('/login') || pathname?.includes('/emergency-login')) {
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
        }

        // 🔒 立即检查一次（初始检查）
        checkAuth(true);

        // 每30秒检查一次JWT是否有效
        checkIntervalRef.current = setInterval(() => {
            checkAuth(false);
        }, 30000); // 30秒

        // 清理定时器
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, [pathname]);

    // 🔒 关键修复：在认证完成前显示加载状态，阻止未授权访问
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-pulse">
                    <div className="text-center">
                        <div className="w-8 h-8 bg-gray-300 rounded-full mx-auto mb-4"></div>
                        <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
                    </div>
                </div>
            </div>
        );
    }

    // 🔒 关键修复：只有认证通过才渲染子组件
    if (!isAuthenticated) {
        return null; // 重定向中，不渲染任何内容
    }

    return (
        <>
            {children}
            {/* 更新日志弹窗 */}
            <ChangelogModal
                show={showModal}
                changelogs={changelogs}
                onClose={closeModal}
                onMarkRead={markRead}
            />
        </>
    );
}